import { Box, Button, Card, CardContent, Link, Typography } from '@mui/material';
import { useCallback, useContext, useMemo, useState } from 'react';
import { Column, Row, Searchbox, Spacer } from '../../components';
import { MutatorState } from '@aesop-fables/scrinium';
import { useOrgSearch } from '../../hooks/useOrgSearch';
import { useNavigateToOrg } from '../../hooks/useNavigateToOrg';
import { OrgStatus } from '../multitier/AdminDashboard';
import { ToastbarContext } from '../../App';
import { useLoading } from '../../hooks/useLoading';
import { Svgs } from '../../assets/svg';
import { GroupData } from '../../models/GroupData';
import { OrganizationData } from '../../models/OrganizationData';
import { Toast } from '../../models/Toast';
import { GroupMetricsData } from '../../models/MetricsData';
import { OrgSearchResults } from '../multitier/OrgSearchResults';
import { SearchResultCard } from '../multitier/SearchResultCard';

export interface GroupListViewProps {
  groupList: GroupData[];
  select: MutatorState<number>;
  selectTeam?: MutatorState<number>;
  allMetrics: GroupMetricsData[];
  selectOrganization?: MutatorState<number>;
  setOrgStatus?: React.Dispatch<React.SetStateAction<OrgStatus | undefined>>;
  setIsNavigatingToOrg?: React.Dispatch<React.SetStateAction<boolean>>;
}

export const GroupListView: React.FC<GroupListViewProps> = ({
  groupList,
  select,
  selectTeam,
  allMetrics,
  selectOrganization,
  setOrgStatus,
  setIsNavigatingToOrg,
}) => {
  const { setLoading } = useLoading();
  const { setToast } = useContext(ToastbarContext);

  const [searchWord, setSearchWord] = useState('');

  const numGroups = groupList?.length ?? 0;
  const trimmedSearch = searchWord.trim();
  const isSearching = trimmedSearch.length > 0;

  const { orgResults, orgSearchLoading } = useOrgSearch({
    setToast,
    trimmedSearch,
    isSearching,
  });

  const filteredGroups = useMemo(() => {
    const sorted = [...groupList].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    if (!isSearching) return sorted;
    const lowerQuery = trimmedSearch.toLowerCase();
    return sorted.filter(group => group.name?.toLowerCase().includes(lowerQuery));
  }, [groupList, isSearching, trimmedSearch]);

  const selectGroup = async (groupId: number) => {
    await select.action(groupId);
  };

  const onViewGroup = async (groupId: number | undefined) => {
    if (groupId === undefined) {
      setToast(new Toast({ message: 'No group id found', severity: 'error', open: true }));
      return;
    }
    setLoading(true);
    selectGroup(groupId);
  };

  const selectParents = useCallback(
    async (org: OrganizationData) => {
      const groupId = org.teamOrgMap?.team?.group?.id;
      const teamId = org.teamOrgMap?.team?.id;
      if (groupId !== undefined) {
        await select.action(groupId);
      }
      if (teamId !== undefined) {
        await selectTeam?.action(teamId);
      }
    },
    [select, selectTeam],
  );

  const onViewOrg = useNavigateToOrg({
    selectOrganization,
    setOrgStatus,
    setIsNavigatingToOrg,
    selectParents,
  });

  const getOrgSubtitle = (org: OrganizationData) => {
    const pathDetail = [org.teamOrgMap?.team?.group?.name, org.teamOrgMap?.team?.name]
      .filter(Boolean)
      .join(' / ');
    return pathDetail ? `Organization \u2022 ${pathDetail}` : 'Organization';
  };

  const hasGroupResults = filteredGroups.length > 0;
  const hasOrgResults = orgResults.length > 0;
  const noResults = isSearching && !hasGroupResults && !hasOrgResults && !orgSearchLoading;

  return (
    <>
      <Spacer height='xs' />
      <Card variant='ghost' color='primary'>
        <CardContent className='searchbox'>
          <Column>
            <Searchbox
              callback={value => setSearchWord(value)}
              label='Search'
              placeholder='Search'
            />
          </Column>
        </CardContent>
      </Card>

      <Spacer height='xs' />
      <Card variant='ghost' color='primary'>
        <CardContent className='pad25'>
          {numGroups === 0 && !isSearching ? (
            <Typography
              variant='p20'
              color='secondary.main'
              textAlign='center'
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              You haven't created any groups yet.
            </Typography>
          ) : isSearching ? (
            <>
              {hasGroupResults && (
                <>
                  <Typography variant='p16Light' color='secondary.dark' sx={{ lineHeight: 1 }}>
                    Groups
                  </Typography>
                  <Spacer height='xxs' />
                  {filteredGroups.map((group, i) => {
                    const metrics = allMetrics.find(x => x.groupId === group.id);
                    const metaDetail = [
                      metrics?.organizations !== undefined &&
                        `${metrics.organizations} Organizations`,
                      metrics?.activeLicenses !== undefined &&
                        `${metrics.activeLicenses} Active Users`,
                    ]
                      .filter(Boolean)
                      .join(' | ');
                    const metaString = metaDetail ? `Group \u2022 ${metaDetail}` : 'Group';

                    return (
                      <SearchResultCard
                        key={group.id}
                        icon={<Svgs.IconGroup />}
                        name={group.name ?? ''}
                        subtitle={metaString}
                        onView={() => onViewGroup(group.id)}
                        isFirst={i === 0}
                      />
                    );
                  })}
                </>
              )}

              {hasGroupResults && (hasOrgResults || orgSearchLoading) && <Spacer height='sm' />}

              <OrgSearchResults
                orgResults={orgResults}
                orgSearchLoading={orgSearchLoading}
                onViewOrg={onViewOrg}
                getSubtitle={getOrgSubtitle}
              />

              {noResults && (
                <Row
                  style={{
                    justifyContent: 'center',
                    padding: '5.5px 0',
                  }}>
                  <Typography variant='p18SemiBold' color='secondary.main'>
                    No results found
                  </Typography>
                </Row>
              )}
            </>
          ) : (
            filteredGroups.map((group, i) => {
              const metrics = allMetrics.find(x => x.groupId === group.id);
              return (
                <Card
                  variant='ghost'
                  color='secondary'
                  key={group.id}
                  sx={{ marginTop: i === 0 ? '0px' : '10px' }}>
                  <CardContent sx={{ padding: '13px 20px !important' }}>
                    <Row style={{ justifyContent: 'space-between' }}>
                      <Row
                        style={{
                          alignItems: 'center',
                        }}>
                        <Svgs.IconGroup />
                      </Row>
                      <Row
                        style={{
                          alignItems: 'center',
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        <Spacer width='xxs' />
                        <Link
                          className='darkenOnHover'
                          noWrap={true}
                          variant='p18SemiBold'
                          color='secondary.main'
                          sx={{ flex: 1 }}
                          onClick={() => {
                            onViewGroup(group.id);
                          }}>
                          {group.name}
                        </Link>
                        <Box sx={{ width: '15px' }} />
                      </Row>

                      <Row style={{ alignItems: 'center' }}>
                        {metrics ? (
                          <>
                            <Column>
                              <Typography variant='p12SemiBold' color='secondary.main'>
                                Organizations
                              </Typography>
                              <Row style={{ alignItems: 'center' }}>
                                <Svgs.IconOrgPaysLarge />
                                <Spacer width='xxs' />
                                <Typography variant='p20SemiBold' color='secondary.main'>
                                  {metrics?.organizations}
                                </Typography>
                              </Row>
                            </Column>
                            <Box sx={{ width: { xs: '15px', lg: '40px' } }} />
                            <Column>
                              <Typography variant='p12SemiBold' color='secondary.main'>
                                Active users
                              </Typography>
                              <Row style={{ alignItems: 'center' }}>
                                <Svgs.IconMembersDefault />
                                <Spacer width='xxs' />
                                <Typography variant='p20SemiBold' color='secondary.main'>
                                  {metrics?.activeLicenses}
                                </Typography>
                              </Row>
                            </Column>
                          </>
                        ) : (
                          <div style={{ padding: '24px ' }} />
                        )}

                        <Box
                          sx={{
                            width: { xs: '15px', md: '20px', lg: '40px' },
                          }}
                        />
                        <Button
                          onClick={() => onViewGroup(group.id)}
                          className='cardAction'
                          color='secondary'>
                          View
                        </Button>
                      </Row>
                    </Row>
                  </CardContent>
                </Card>
              );
            })
          )}
        </CardContent>
      </Card>
    </>
  );
};
